import { type FC, type ReactNode, useMemo } from 'react';
import { Card } from '../Card';
import { CompletedTrainingCard } from './components/CompletedTrainingCard';
import styled from 'styled-components';
import { VoluntaryTrainingCard } from './components/VoluntaryTrainingCard';
import { MandatoryTrainingCard } from './components/MandatoryTrainingCard';
import AlertIcon from '../../assets/AlertIcon.svg';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import {
  type EnrolledCampaignListResponse,
  useCampaignsQuery,
  type VoluntaryItem,
  type VoluntaryTrainingListResponse,
} from '../../apiHooks/useCampaignsQuery';
import { useState } from 'react';
import { LabelledToggle } from '../Toggle';
import type { LabelledToggleOption } from '../Toggle/LabelledToggle/LabelledToggle';
import { RTLFixSpan } from './utils/RTLFixSpan';
import { NoMandatoryTrainingsCard } from './components/NoMandatoryTrainingsCard';
import { useCyberIqSettingsQuery } from '../../apiHooks/useCyberIqSettingsQuery';
import { useMeQuery } from '../../apiHooks/useMeQuery';

export const TrainingWidget: FC = () => {
  const {
    data: {
      completed: completedCampaigns,
      mandatory: mandatoryCampaigns,
      voluntary: voluntary,
      voluntaryModules: voluntaryCampaigns,
    },
    refetch,
  } = useCampaignsQuery(true);
  const { data: meData } = useMeQuery();
  const { data: cyberIqSettings } = useCyberIqSettingsQuery(meData?.learnerId ?? '');
  const isVoluntaryTrainingsActive: boolean = cyberIqSettings?.voluntaryTraining || false;
  const voluntaryTrainings: VoluntaryItem[] = [...voluntary, ...voluntaryCampaigns];

  const urgentCampaigns = useMemo(() => {
    if (!mandatoryCampaigns.length) {
      return [];
    }

    return mandatoryCampaigns.filter((campaign: EnrolledCampaignListResponse) => {
      const now: Date = new Date();
      const dueDate: Date = new Date(campaign.dueDate);
      const timeDifference = dueDate.getTime() - now.getTime();
      const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

      return daysDifference < 2;
    });
  }, [mandatoryCampaigns]);

  const handleCampaignComplete = () => {
    refetch();
  };

  const [selectedTrainingType, setSelectedTrainingType] = useState<'mandatory' | 'voluntary' | 'completed'>(
    'mandatory',
  );

  const toggleLabels: LabelledToggleOption[] = [
    {
      label: <NamedContentEntryText container="span" refKey="yourTraining" subKey="mandatory" />,
      value: 'mandatory',
      selected: selectedTrainingType === 'mandatory',
    },
    ...(isVoluntaryTrainingsActive
      ? [
          {
            label: <NamedContentEntryText container="span" refKey="yourTraining" subKey="voluntary" />,
            value: 'voluntary',
            selected: selectedTrainingType === 'voluntary',
          },
        ]
      : []),
    {
      label: <NamedContentEntryText container="span" refKey="yourTraining" subKey="completed" />,
      value: 'completed',
      selected: selectedTrainingType === 'completed',
    },
  ];

  const onFilterChange = (index: number) => {
    setSelectedTrainingType(toggleLabels[index]?.value as 'mandatory' | 'voluntary' | 'completed');
  };

  return (
    <Card
      title={<NamedContentEntryText container={'span'} refKey="sectionTitles" subKey="yourTraining" />}
      rightElem={
        !!urgentCampaigns.length && (
          <HeaderRightElement>
            <AlertIcon />
            <label>
              <NamedContentEntryText container={'span'} refKey="yourTraining" subKey="urgent" />:{' '}
              <RTLFixSpan>{urgentCampaigns.length}</RTLFixSpan>
            </label>
          </HeaderRightElement>
        )
      }
    >
      <FiltersBar>
        <LabelledToggle
          fullWidth
          labels={toggleLabels}
          onSelect={(selectedIndex: number) => onFilterChange(selectedIndex)}
        />
      </FiltersBar>
      <SectionContent>
        {selectedTrainingType === 'mandatory' && (
          <MandatoryCampaignList
            campaigns={mandatoryCampaigns}
            render={(campaign: EnrolledCampaignListResponse) => (
              <MandatoryTrainingCard key={campaign.id} campaign={campaign} />
            )}
          />
        )}
        {selectedTrainingType === 'voluntary' && (
          <VoluntaryCampaignList
            campaigns={voluntaryTrainings}
            render={(campaign: VoluntaryTrainingListResponse | EnrolledCampaignListResponse) => (
              <VoluntaryTrainingCard key={campaign.id} campaign={campaign} onComplete={handleCampaignComplete} />
            )}
          />
        )}
        {selectedTrainingType === 'completed' && (
          <CompletedCampaignList
            campaigns={completedCampaigns}
            render={(campaign: EnrolledCampaignListResponse) => (
              <CompletedTrainingCard key={campaign.id} campaignUrl={campaign.campaignUrl} name={campaign.name} />
            )}
          />
        )}
      </SectionContent>
    </Card>
  );
};

type CampaignListProps = {
  campaigns: EnrolledCampaignListResponse[];
  render: (campaign: EnrolledCampaignListResponse) => ReactNode;
};

type VoluntaryTrainingListProps = {
  campaigns: VoluntaryItem[];
  render: (campaign: VoluntaryItem) => ReactNode;
};

const MandatoryCampaignList: FC<CampaignListProps> = ({ campaigns, render }: CampaignListProps) => {
  if (!campaigns.length) {
    return <NoMandatoryTrainingsCard />;
  }

  return campaigns.map((campaign: EnrolledCampaignListResponse) => render(campaign));
};

const CompletedCampaignList: FC<CampaignListProps> = ({ campaigns, render }: CampaignListProps) => {
  if (!campaigns.length) {
    return (
      <NoTrainingsText>
        <NamedContentEntryText container={'span'} refKey={'yourTraining'} subKey={'noCompletedTrainings'} />
      </NoTrainingsText>
    );
  }

  return campaigns.map((campaign: EnrolledCampaignListResponse) => render(campaign));
};

const VoluntaryCampaignList: FC<VoluntaryTrainingListProps> = ({ campaigns, render }: VoluntaryTrainingListProps) => {
  if (!campaigns.length) {
    return (
      <NoTrainingsText>
        <NamedContentEntryText container={'span'} refKey={'yourTraining'} subKey={'noVoluntaryTrainings'} />
      </NoTrainingsText>
    );
  }

  return campaigns.map((campaign: VoluntaryItem) => render(campaign));
};

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 10px;
`;

const HeaderRightElement = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.urgent};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 500;
  text-transform: none;
`;

const FiltersBar = styled.div`
  display: flex;
  width: 100%;
`;

const NoTrainingsText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: ${({ theme }) => theme.colors.normal};
  font-weight: 400;
  text-align: center;
`;
